import { ElementRef, Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppLoaderService, CommonActionKey, MessageService, SimpleDialogComponent } from "common";
import { isEqual } from "lodash";
import { of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged } from "rxjs/operators";
import { SubmissionDetailsActionsService } from "../../../../../actions/submission-details-actions.service";
import { getUnableToSaveDialogData } from "../../../form-details.data";
import { AutosaveTriggerData, AutosaveTriggerStatus } from "../../../form-details.model";
import { AutosaveFormDetailsFacade } from "../../../services/autosave/autosave-form-details.facade";

@UntilDestroy()
@Injectable()
export class TriggersService {
  constructor(
    private readonly autosaveFormDetailsFacade: AutosaveFormDetailsFacade,
    private readonly submissionDetailsActions: SubmissionDetailsActionsService,
    private readonly messageService: MessageService,
    private readonly matDialog: MatDialog,
    private readonly appLoaderService: AppLoaderService,
    private readonly elementRef: ElementRef,
  ) { }

  initTriggers(submissionForm: FormGroup, abortController: AbortController): void {
    this.initValueChangesAction(submissionForm);
    this.watchCompanyNameChanges(submissionForm, abortController);
    this.initClosePageRequestAction(submissionForm);
  }

  handleBeforeUnloadAction(event: BeforeUnloadEvent, submissionForm: FormGroup): void {
    const data: AutosaveTriggerData = this.autosaveFormDetailsFacade.autosaveFormData(submissionForm);
    if (data.status === AutosaveTriggerStatus.Blocked) {
      event.preventDefault();
      return;
    }

    if (this.autosaveFormDetailsFacade.isAutosaveInProgress()) {
      this.messageService.info("We'll notify you once it's done!", "Saving your changes...");
      this.autosaveFormDetailsFacade.waitForAutosaveCompletion()
        .pipe(untilDestroyed(this))
        .subscribe((hasAnyChangesSaved: boolean) => {
          if (hasAnyChangesSaved) {
            this.showSuccessToastMessage();
          }
        })
      event.preventDefault();
    }
  }

  private initValueChangesAction(submissionForm: FormGroup): void {
    submissionForm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(2000),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr))
      )
      .subscribe(() => {
        if (this.isNameInputActive()) {
          return;
        }

        this.autosaveFormDetailsFacade.autosaveFormData(submissionForm);
      });
  }

  private watchCompanyNameChanges(submissionForm: FormGroup, abortController: AbortController): void {
    const nameInput = this.getNameInputElement();
    if (!nameInput) {
      return;
    }

    nameInput.addEventListener('blur', () => {
      this.autosaveFormDetailsFacade.autosaveFormData(submissionForm);
    }, { signal: abortController.signal });
  }

  private initClosePageRequestAction(submissionForm: FormGroup): void {
    this.submissionDetailsActions.canDeactivateRequest$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.handleClosePageRequest(submissionForm));
  }

  private handleClosePageRequest(submissionForm: FormGroup): void {
    const data: AutosaveTriggerData = this.autosaveFormDetailsFacade.autosaveFormData(submissionForm);
    if (data.status === AutosaveTriggerStatus.Blocked) {
      return this.handleBlockedClosePageRequest(submissionForm);
    }

    this.appLoaderService.setManualLoading(true);
    this.autosaveFormDetailsFacade.waitForAutosaveCompletion()
      .pipe(
        untilDestroyed(this),
        catchError(() => of(undefined))
      )
      .subscribe((hasAnyChangesSaved: boolean) => {
        this.sendCanDeactivateEvent(true);
        this.appLoaderService.setManualLoading(false);
        if (hasAnyChangesSaved) {
          this.showSuccessToastMessage();
        }
      });
  }

  private handleBlockedClosePageRequest(submissionForm: FormGroup): void {
    if (!!sessionStorage.getItem("sessionExpiredAutoLogout")) {
      this.sendCanDeactivateEvent(true);
      this.showSessionExpiredMessage();
      return;
    }
    SimpleDialogComponent.show(this.matDialog, getUnableToSaveDialogData())
      .pipe(untilDestroyed(this))
      .subscribe((actionKey: string) => {
        if (actionKey === CommonActionKey.No) {
          this.enableNameFieldValidation(submissionForm);
        }

        const deactivateEventValue = actionKey === CommonActionKey.Yes;
        this.sendCanDeactivateEvent(deactivateEventValue);
      });
  }

  private sendCanDeactivateEvent(canDeactivate: boolean): void {
    setTimeout(() => {
      this.submissionDetailsActions.canDeactivateResponse(canDeactivate);
    }, 0);
  }

  private isNameInputActive(): boolean {
    return this.getNameInputElement() === document.activeElement;
  }

  private getNameInputElement(): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector('[formControlName="name"]');
  }

  private enableNameFieldValidation(submissionForm: FormGroup): void {
    submissionForm.get("businessInformation.name").markAsTouched();
  }

  private showSuccessToastMessage(): void {
    this.messageService.success("Your submission has been successfully saved.");
  }

  private showSessionExpiredMessage(): void {
    this.messageService.warning("We couldn't save your submission because a company name is missing and your session expired.", "Submission not saved", true);
  }
}
